import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {calculateLogLineHeight} from '../layoutUtils';
import {type LogLineData} from '../LogLineRenderer';

export interface UseLogLineHeightEstimatesParams {
  /** The Virtuoso scroller element; used for width measurement. */
  scrollerElement: HTMLElement | null;

  /** The default line height (default vs. compact). */
  lineHeight: number;
  /** The list of log lines to be rendered. */
  lines: readonly LogLineData[];

  /** The maximum label length (used for alignment). */
  maxLabelLength: number;
  /** The maximum line number length (used for alignment). */
  maxLineNumLength: number;

  /** Whether labels are visible for log lines. */
  showLabel: boolean;
  /** Whether line numbers are visible for log lines. */
  showLineNumber: boolean;
  /** Whether timestamps are visible for log lines. */
  showTimestamp: boolean;
}

export interface UseLogLineHeightEstimatesResult {
  /** An array of estimated heights for each log line. These should be passed to
   * `Virtuoso` as log lines change. */
  heightEstimates: number[] | null;
  /** A hidden element used to measure character width and scrollbar width;
   * render in the same CSS context as the actual log lines. */
  measurer: React.ReactElement;
}

/**
 * Estimates the rendered height of each log line based on content length and
 * available width.
 *
 * This hook calculates how much horizontal space is consumed by metadata like
 * line numbers, timestamps, and labels and uses the remaining width to estimate
 * how many lines the content will wrap to. It also accounts for flex-wrap
 * behavior on mobile viewports. This provides smooth virtualized scrolling by
 * giving 'Virtuoso' accurate height estimates.
 */
export function useLogLineHeightEstimates({
  lines,
  scrollerElement,
  showTimestamp,
  showLabel,
  showLineNumber,
  maxLineNumLength,
  maxLabelLength,
  lineHeight,
}: UseLogLineHeightEstimatesParams): UseLogLineHeightEstimatesResult {
  const {charWidthPx, measurer: charMeasurer} = useCharacterMeasurement();
  const {scrollbarWidthPx, measurer: scrollbarMeasurer} =
    useScrollbarMeasurement();
  const containerWidthPx = useContainerWidth(scrollerElement, scrollbarWidthPx);

  const combinedMeasurer = (
    <>
      {charMeasurer}
      {scrollbarMeasurer}
    </>
  );

  let heightEstimates: number[] | null;

  // Return default heights until measurements are available.
  if (!containerWidthPx || !charWidthPx) {
    heightEstimates = null;
  } else {
    heightEstimates = lines.map(line =>
      calculateLogLineHeight({
        containerWidthPx,
        charWidthPx,
        lineHeightPx: lineHeight,
        content: line.line,
        maxLabelLength,
        maxLineNumLength,
        showLabel,
        showLineNumber,
        showTimestamp,
      })
    );
  }

  return {
    heightEstimates,
    measurer: combinedMeasurer,
  };
}

function useContainerWidth(
  container: HTMLElement | null,
  scrollbarWidthPx: number | null
): number | null {
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!container || scrollbarWidthPx === null) {
      return;
    }

    const updateContainerWidth = () => {
      const clientWidth = container.clientWidth;
      const hasScrollbar = container.scrollHeight > container.clientHeight;

      const width = hasScrollbar ? clientWidth : clientWidth - scrollbarWidthPx;

      if (width > 0) {
        setContainerWidth(width);
      }
    };

    const observer = new ResizeObserver(updateContainerWidth);
    observer.observe(container);

    updateContainerWidth(); // Initialize container width.

    return () => observer.disconnect();
  }, [container, scrollbarWidthPx]);

  return containerWidth;
}

function useCharacterMeasurement(measurementString: string = '0'.repeat(10)): {
  charWidthPx: number | null;
  measurer: React.ReactElement;
} {
  const nodeRef = useRef<HTMLSpanElement | null>(null);
  const [charWidthPx, setCharWidthPx] = useState<number | null>(null);

  const measure = useCallback(() => {
    if (nodeRef.current) {
      const width = nodeRef.current.getBoundingClientRect().width;
      if (width > 0) {
        setCharWidthPx(width / measurementString.length);
      }
    }
  }, [measurementString]);

  const setRef = useCallback(
    (node: HTMLElement | null) => {
      nodeRef.current = node;
      measure();
    },
    [measure]
  );

  // Re-measure on resize (font rendering can vary at different zoom levels).
  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  // 'measurer' is a hidden element used to measure the width of a single
  // character. This must be rendered in the same CSS context as actual log
  // lines.
  const measurer = useMemo(
    () => (
      <span
        aria-hidden
        ref={setRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}>
        {measurementString}
      </span>
    ),
    [measurementString, setRef]
  );

  return {
    charWidthPx,
    measurer,
  };
}

function useScrollbarMeasurement(): {
  scrollbarWidthPx: number | null;
  measurer: React.ReactElement;
} {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [scrollbarWidthPx, setScrollbarWidthPx] = useState<number | null>(null);

  const measure = () => {
    if (outerRef.current && innerRef.current) {
      const width = outerRef.current.offsetWidth - innerRef.current.offsetWidth;
      setScrollbarWidthPx(width);
    }
  };

  const setOuterRef = (node: HTMLDivElement | null) => {
    outerRef.current = node;
    measure();
  };

  const setInnerRef = (node: HTMLDivElement | null) => {
    innerRef.current = node;
    measure();
  };

  // 'measurer' is a hidden element used to measure the scrollbar width. It
  // creates a scrollable container and measures the difference between outer
  // and inner widths.
  const measurer = (
    <div
      aria-hidden
      ref={setOuterRef}
      style={{
        position: 'absolute',
        visibility: 'hidden',
        overflow: 'scroll',
        width: '100px',
        height: '100px',
        pointerEvents: 'none',
      }}>
      <div ref={setInnerRef} style={{width: '100%', height: '100%'}} />
    </div>
  );

  return {
    scrollbarWidthPx,
    measurer,
  };
}
